import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { WebService, lastValueFrom, PromptService } from '@services';
import { RmsModule } from '@rms';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { OrdersDataService } from '../../../../../services/orders-data.service';
import { InventoryDataService } from '../../../../../services/inventory-data.service';
import { CustomerReservesOrdersComponent } from '../customer-reserves-orders/customer-reserves-orders.component';
import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';

const ALLOWED_WAREHOUSE_IDS = [37, 45, 31];

interface CustomerDefaults {
  CompanyID: number;
  CompanyName: string;
  ShippingStreet: string; ShippingCity: string; ShippingState: string; ShippingZip: string;
  CurrentTraderID: number;
  LastDOInstructions: string | null;
  LastInvoiceInstructions1: string | null;
  LastInvoiceInstructions2: string | null;
  LastShrinkWrap: boolean | null;
  LastSlipSheet: boolean | null;
  LastPalletize: boolean | null;
  LastMorningPickup: boolean | null;
  LastCustomerPickup: boolean | null;
  LastReweighOrder: boolean | null;
  LastTruckingQuote: string | null;
  LastPaymentTerms: number | null;
  DefaultPaymentMethodID: number;
  LastOrderWhse?: number;
  LastTrucker?: number;
}

@Component({
  selector: 'app-order-form',
  imports: [RmsModule, ReactiveFormsModule, MatStepperModule, CustomerReservesOrdersComponent],
  templateUrl: './order-form.component.html',
  styleUrl: './order-form.component.scss'
})
export class OrderFormComponent implements OnInit {
  readonly StepLabels = [
    'Customer & Order',
    'Coffee Selection',
    'Shipping & Delivery',
    'Payment & Validation',
    'Review & Submit'
  ];

  ActiveOrderID: number | null = null;
  BoardOrderStatusID = 1;
  get LiveBoardSubmission(): boolean { return !!this.pulldownData?.LiveBoard; }

  PrefilledCompanyID: number | null = null;
  get CustomerLocked(): boolean {
    return !!this.PrefilledCompanyID && !this.ActiveOrderID;
  }

  CustomerOrderForm: FormGroup;
  ShippingDeliveryForm: FormGroup;

  OrderLineItems: any[] = [];
  ShippingAddresses: any[] = [];
  ContactAddresses:any[]=[];BillingAddressID:number|null=null;ShippingAddressID:number|null=null;BillingAddress:any=null;
  contactOptions(type:string){return this.ContactAddresses.filter(a=>a.AddressType===type&&a.IsActive&&a.ContactActive&&a.CustomerContactID);}
  contactLabel(a:any){return [a.FirstName,a.LastName].filter(Boolean).join(' ')+' — '+[a.Street,a.City].filter(Boolean).join(', ');}
  selectContactAddress(type:string,id:number|null){const a=this.contactOptions(type).find(x=>x.CustomerShipToAddressID===Number(id));if(type==='Billing'){this.BillingAddress=a?{...a}:null;}else{this.ShippingAddresses=a?[{...a,IsPrimary:true}]:[];}this.approveReview(false);}

  WarehouseNumber: number | null = null;
  WarehouseSelection: number | null = null;
  WarehouseChanging = false;
  private capacityRequest = 0;
  readonly StepDescriptions = [
    'Select the customer and confirm their order preferences.',
    'Build one order from available reserves and spot coffee.',
    'Confirm delivery details, dates and handling instructions.',
    'Review required fields, pricing and payment status.',
    'Check the complete order and approve it for submission.'
  ];

  BlockedDates: string[] = [];

  SelectedCustomer: CustomerDefaults | null = null;
  Saving = false;
  Submitting = false;
  ActionError = '';
  OrderStatusName = 'Draft';
  DatePaid: string | null = null;
  CustomerOrders: any[] = [];
  HistoryLoading = false;
  HistoryError = '';
  PriorOrder: any = null;
  private customerRequest = 0;
  private savedSnapshot = '';
  private approvedSnapshot = '';
  private readonly newOrderSaleDate = new Date().toISOString();

  PaymentTerms: any[] = [];
  PaymentMethods: any[] = [];
  Warehouses: any[] = [];
  Truckers: any[] = [];
  Traders: any[] = [];
  Customers: any[] = [];

  PriceExceptions: any[] = [];

  // NEW (5.2.23): inline "add new carrier" quick-form state, shown next to
  // the Trucker picker so a trader isn't blocked waiting on someone else to
  // add a carrier elsewhere in the system.
  AddingTrucker = false;
  NewTruckerName = '';

  OrderSourceOptions = [
    { label: 'Phone', value: 'Phone' },
    { label: 'Email', value: 'Email' },
    { label: 'Web', value: 'Web' },
  ];


  currentStepIndex = 0;

  constructor(
    private fb: FormBuilder,
    private webService: WebService,
    private ordersData: OrdersDataService,
    private inventoryData: InventoryDataService,
    private prompt: PromptService,
    private pulldownRef: RmsPulldownRef,
    @Inject(RMS_PULLDOWN_DATA) private pulldownData: { OrderID?: number; CompanyID?: number; LiveBoard?: boolean } | null,
    private cdr: ChangeDetectorRef
  ) {
    this.CustomerOrderForm = this.fb.group({
      CompanyID: [null, Validators.required],
      CompanyName: [{ value: '', disabled: true }],
      Trader: [null, Validators.required],
      OrderSource: ['Phone', Validators.required],
      ProposedPickupDate: [null, Validators.required],
      PaymentMethodID: [null, Validators.required],
      PaymentTerms: [null],
      Trucker: [null],
    });

    this.ShippingDeliveryForm = this.fb.group({
      WarehouseInstructions: [null],
      InvoiceInstructions1: [null],
      InvoiceInstructions2: [null],
      ShrinkWrap: [false],
      SlipSheet: [false],
      Palletize: [false],
      MorningPickup: [false],
      CustomerPickup: [false],
      ReweighOrder: [false],
      TruckingQuote: [null],
      ProNumber: [null],
      PickupDate: [null],
    });

    // Mirrors the customer's requested date into the confirmed Pickup Date
    // field the first time it's set, so a trader isn't forced to re-enter a
    // date that's already sitting right there — but only while the field is
    // still untouched, so it never overwrites something someone deliberately changed.
    this.CustomerOrderForm.get('ProposedPickupDate')?.valueChanges.subscribe((newDate: string) => {
      const pickupControl = this.ShippingDeliveryForm.get('PickupDate');
      if (pickupControl && !pickupControl.value) {
        pickupControl.setValue(newDate);
      }
    });


  }

  async ngOnInit() {
    this.ActiveOrderID = this.pulldownData?.OrderID ?? null;
    this.PrefilledCompanyID = this.pulldownData?.CompanyID ?? null;

    const orderRef = await this.ordersData.getRefData({ keys: ['PaymentMethods'] });
    this.PaymentMethods = orderRef.PaymentMethods ?? [];

    const lookupRef: any = await this.inventoryData.getData({
      keys: ['PaymentTerms', 'DestinationWarehouses', 'Truckers'],
      includeStatic: false
    });
    this.PaymentTerms = lookupRef.PaymentTerms ?? [];

    // 5.2.23: only show active truckers in the picker, and support newly-
    // added ones showing up without carrying old inactive rows into the list.
    this.Truckers = (lookupRef.Truckers ?? []).filter((t: any) => t.IsActive);

    this.Warehouses = (lookupRef.DestinationWarehouses ?? [])
      .filter((w: any) => ALLOWED_WAREHOUSE_IDS.includes(w.LocationID));

    this.Traders = await lastValueFrom(this.webService.getWebCall('data', 'coffee/traders'));

    this.Customers = await lastValueFrom(
      this.webService.getWebCall('customers', 'coffee/customers', {
        params: [{ name: 'IncludeInactive', value: 0 }]
      })
    );

    if (this.ActiveOrderID) {
      await this.loadExistingOrder(this.ActiveOrderID);
    } else if (this.PrefilledCompanyID) {
      const match = this.Customers.find(c => c.CompanyID === this.PrefilledCompanyID);
      await this.loadCustomerDefaults(this.PrefilledCompanyID, match?.CompanyName, true);
    }
    this.cdr.markForCheck();
  }

  onStepTabClick(index: number) {
    this.currentStepIndex = index;
  }

  async requestWarehouseChange(warehouseNumber: number | null) {
    this.WarehouseSelection=warehouseNumber;
    if(warehouseNumber===this.WarehouseNumber) return;
    this.WarehouseChanging=true;
    try {
      if(this.OrderLineItems.length) {
        const ref=this.prompt.open({
          promptText:'Changing the warehouse will clear the coffees in this order. Customer and delivery details will be kept. Continue?',
          buttons:[{label:'Change warehouse',value:true,buttonClass:'btn-bg-blue'}]
        });
        if(!await firstValueFrom(ref.closed$.pipe(take(1)))) {
          this.WarehouseSelection=this.WarehouseNumber; return;
        }
        this.OrderLineItems=[];
        this.PriceExceptions=[];
      }
      await this.onWarehouseChanged(warehouseNumber);
    } finally { this.WarehouseChanging=false; this.cdr.markForCheck(); }
  }

  async onWarehouseChanged(warehouseNumber: number | null) {
    this.WarehouseNumber=warehouseNumber;
    this.WarehouseSelection=warehouseNumber;
    this.BlockedDates=[];
    const request=++this.capacityRequest;
    if(!warehouseNumber) return;
    try {
      const capacity=await lastValueFrom(this.webService.getWebCall('outbound','capacity'));
      if(request!==this.capacityRequest) return;
      this.BlockedDates=capacity.filter((c:any)=>c.WarehouseID===warehouseNumber && (c.IsClosed || c.TodayPastCutoff))
        .map((c:any)=>c.PickupDate.split('T')[0]);
    } catch {
      if(request===this.capacityRequest) this.ActionError='Warehouse pickup availability could not be loaded. Please reselect the warehouse to try again.';
    }
    this.cdr.markForCheck();
  }

  onLineItemsChanged(items: any[]) {
    this.OrderLineItems = items;
  }

  removeLineItem(inventoryId: number) {
    this.OrderLineItems = this.OrderLineItems.filter(i => i.InventoryID !== inventoryId);
  }

  async loadExistingOrder(orderId: number) {
    const result = await lastValueFrom(this.ordersData.getOrderDetails(orderId));
    const order = result?.order;
    if (!order) return;

    if (order.CompanyID) {
      await this.loadCustomerDefaults(order.CompanyID, order.CompanyName, false);
    }

    this.CustomerOrderForm.patchValue({
      CompanyID: order.CompanyID,
      CompanyName: order.CompanyName,
      Trader: order.Trader,
      OrderSource: order.OrderSource ?? 'Phone',
      ProposedPickupDate: order.ProposedPickupDate,
      PaymentMethodID: order.PaymentMethodID,
      PaymentTerms: order.PaymentTerms,
      Trucker: order.Trucker,
    });

    this.WarehouseNumber = order.WarehouseNumber ?? null;

    this.ShippingDeliveryForm.patchValue({
      WarehouseInstructions: order.WarehouseInstructions,
      InvoiceInstructions1: order.InvoiceInstructions1,
      InvoiceInstructions2: order.InvoiceInstructions2,
      ShrinkWrap: order.ShrinkWrap ?? false,
      SlipSheet: order.SlipSheet ?? false,
      Palletize: order.Palletize ?? false,
      MorningPickup: order.MorningPickup ?? false,
      CustomerPickup: order.CustomerPickup ?? false,
      ReweighOrder: order.ReweighOrder ?? false,
      TruckingQuote: order.TruckingQuote,
      ProNumber: order.ProNumber,
      PickupDate: order.PickupDate,
    });

    if (this.WarehouseNumber) {
      await this.onWarehouseChanged(this.WarehouseNumber);
    }

    this.BillingAddressID=order.BillingAddressID??null;this.ShippingAddressID=order.ShippingAddressID??null;this.BillingAddress=order.BillingAddress??null;
    this.ShippingAddresses = order.ShippingAddresses?.length ? order.ShippingAddresses : [];
    this.OrderLineItems = result?.lineItems ?? [];
    this.PriceExceptions = order.PriceExceptions ?? [];
    this.OrderStatusName = order.StatusName ?? 'Draft';
    this.DatePaid = order.DatePaid ?? null;
    this.savedSnapshot = JSON.stringify(this.buildFullPayload());
  }

  async onCustomerSelected(companyId: number) {
    if (!companyId) return;
    const previousId = this.SelectedCustomer?.CompanyID;
    if (previousId && previousId !== companyId && this.OrderLineItems.length) {
      const confirmation = this.prompt.open({
        promptText: 'Changing the customer will clear the selected coffees and reload customer defaults. Continue?',
        buttons: [{ label: 'Change Customer', value: true, buttonClass: 'btn-bg-blue' }]
      });
      if (!await firstValueFrom(confirmation.closed$.pipe(take(1)))) {
        this.CustomerOrderForm.get('CompanyID')?.setValue(previousId, { emitEvent: false });
        return;
      }
      this.OrderLineItems = [];
      this.PriceExceptions = [];
      this.DatePaid = null;
    }
    const match = this.Customers.find(c => c.CompanyID === companyId);
    await this.loadCustomerDefaults(companyId, match?.CompanyName, true);
  }

  private async loadCustomerDefaults(companyId: number, companyName?: string, seedForm: boolean = true) {
    const request = ++this.customerRequest;
    const result = await lastValueFrom(
      this.webService.getWebCall('customers', `coffee/customers/${companyId}`)
    );
    if (request !== this.customerRequest) return;
    const customer: CustomerDefaults = Array.isArray(result) ? result[0] : result;
    // CRM preferences are the approved defaults; prior orders remain the fallback.
    try {
      const preference = await lastValueFrom(this.webService.getWebCall('customers', `coffee/customers/${companyId}/payment-preference`));
      if (request !== this.customerRequest) return;
      if (preference?.PaymentMethodID != null) customer.DefaultPaymentMethodID = preference.PaymentMethodID;
      if (preference?.PaymentTermId != null) customer.LastPaymentTerms = preference.PaymentTermId;
    } catch {
      if (request !== this.customerRequest) return;
      this.ActionError = 'Customer payment preferences could not be loaded. Check the payment fields before saving.';
    }
    if (request !== this.customerRequest) return;
    this.SelectedCustomer = customer;
    this.CustomerOrders = [];
    this.PriorOrder = null;
    void this.loadCustomerOrderHistory(companyId, request);

    this.CustomerOrderForm.patchValue({
      CompanyID: customer?.CompanyID ?? companyId,
      CompanyName: customer?.CompanyName ?? companyName ?? '',
      Trader: customer?.CurrentTraderID ?? null,
    });

    this.ContactAddresses=[];
    if(seedForm){this.BillingAddressID=null;this.ShippingAddressID=null;this.BillingAddress=null;}
    try{const addresses=await lastValueFrom(this.webService.getWebCall('customers',`coffee/customers/${companyId}/ship-to-addresses`));if(request!==this.customerRequest)return;this.ContactAddresses=addresses||[];}catch{if(request!==this.customerRequest)return;this.ActionError='Customer contacts could not be loaded. Check billing and shipping before saving.';}
    if (!seedForm) return;

    this.CustomerOrderForm.patchValue({
      PaymentTerms: customer?.LastPaymentTerms ?? null,
      PaymentMethodID: customer?.DefaultPaymentMethodID ?? 1,
      Trucker: customer?.LastTrucker ?? null,
    });

    this.ShippingDeliveryForm.patchValue({
      WarehouseInstructions: customer?.LastDOInstructions ?? null,
      InvoiceInstructions1: customer?.LastInvoiceInstructions1 ?? null,
      InvoiceInstructions2: customer?.LastInvoiceInstructions2 ?? null,
      ShrinkWrap: customer?.LastShrinkWrap ?? false,
      SlipSheet: customer?.LastSlipSheet ?? false,
      Palletize: customer?.LastPalletize ?? false,
      MorningPickup: customer?.LastMorningPickup ?? false,
      CustomerPickup: customer?.LastCustomerPickup ?? false,
      ReweighOrder: customer?.LastReweighOrder ?? false,
      TruckingQuote: customer?.LastTruckingQuote ?? null,
    });

    this.ShippingAddresses = customer?.ShippingStreet ? [{
        Street: customer.ShippingStreet,
        City: customer.ShippingCity,
        State: customer.ShippingState,
        ZipCode: customer.ShippingZip,
        Country: 'USA',
        IsPrimary: true
      }] : [];
    const billing=this.contactOptions('Billing').find(a=>a.IsDefault);
    const shipping=this.contactOptions('Shipping').find(a=>a.IsDefault);
    if(billing){this.BillingAddressID=billing.CustomerShipToAddressID;this.selectContactAddress('Billing',this.BillingAddressID);}
    if(shipping){this.ShippingAddressID=shipping.CustomerShipToAddressID;this.selectContactAddress('Shipping',this.ShippingAddressID);}
    const warehouse = customer?.LastOrderWhse;
    await this.onWarehouseChanged(warehouse && this.Warehouses.some(w => w.LocationID === warehouse) ? warehouse : null);
  }

  private async loadCustomerOrderHistory(companyId: number, request: number) {
    this.HistoryLoading = true;
    this.HistoryError = '';
    try {
      const orders = await lastValueFrom(this.ordersData.getSalesOrders({ CompanyID: companyId }));
      if (request === this.customerRequest) this.CustomerOrders = (orders ?? []).sort((a: any, b: any) => b.OrderID - a.OrderID);
    } catch {
      if (request === this.customerRequest) this.HistoryError = 'Customer order history could not be loaded.';
    } finally {
      if (request === this.customerRequest) { this.HistoryLoading = false; this.cdr.markForCheck(); }
    }
  }

  async viewPriorOrder(orderId: number) {
    const request = this.customerRequest;
    this.HistoryError = '';
    try {
      const result = await lastValueFrom(this.ordersData.getOrderDetails(orderId));
      if (request === this.customerRequest) this.PriorOrder = result;
    } catch { this.HistoryError = 'Order details could not be loaded. Please try again.'; }
    this.cdr.markForCheck();
  }

  addShippingAddress() {
    this.ShippingAddresses.push({ Street: '', City: '', State: '', ZipCode: '', Country: 'USA', IsPrimary: false });
  }

  removeShippingAddress(index: number) {
    this.ShippingAddresses.splice(index, 1);
    if (this.ShippingAddresses.length && !this.ShippingAddresses.some(a => a.IsPrimary)) this.ShippingAddresses[0].IsPrimary = true;
  }

  setPrimaryAddress(index: number) {
    this.ShippingAddresses.forEach((addr, i) => addr.IsPrimary = (i === index));
  }

  get requiredFieldsComplete(): boolean {
    return this.validationIssues.length === 0;
  }

  get validationIssues(): { message: string; step: number }[] {
    const issues: { message: string; step: number }[] = [];
    if (!this.CustomerOrderForm.valid) issues.push({ message: 'Complete the required customer, trader, requested date, and payment fields.', step: 0 });
    if (!this.WarehouseNumber) issues.push({ message: 'Select a warehouse.', step: 1 });
    if (!this.OrderLineItems.length) issues.push({ message: 'Add at least one coffee to the order.', step: 1 });
    if (this.OrderLineItems.some(i => !Number.isInteger(i.NumberOfBags) || i.NumberOfBags <= 0 || !Number.isFinite(i.Price) || i.Price <= 0)) issues.push({ message: 'Every coffee needs a positive whole bag quantity and a valid price.', step: 1 });
    if (!this.CustomerOrderForm.get('Trucker')?.value && !this.ShippingDeliveryForm.get('CustomerPickup')?.value) issues.push({ message: 'Select a carrier or choose Customer pickup in shipping options.', step: 0 });
    const pickup = this.ShippingDeliveryForm.get('PickupDate')?.value;
    if (pickup && this.BlockedDates?.includes(String(pickup).slice(0,10))) issues.push({ message: 'The selected warehouse is closed or past cutoff for the confirmed pickup date. Choose another date.', step: 2 });
    if (!this.ShippingDeliveryForm.valid) issues.push({ message: 'Check the shipping and delivery fields.', step: 2 });
    if ((!this.ActiveOrderID || this.BillingAddressID || this.ShippingAddressID) && (!this.BillingAddressID || !this.ShippingAddressID)) issues.push({message:'Choose billing and shipping contacts. Link their addresses in Roaster CRM if needed.',step:2});
    if (!this.ShippingAddresses.length || this.ShippingAddresses.some(a => !a.Street?.trim() || !a.City?.trim() || !a.State?.trim() || !a.ZipCode?.trim())) issues.push({ message: 'Complete each delivery address (street, city, state, and ZIP).', step: 2 });
    if (this.ShippingAddresses.filter(a => a.IsPrimary).length !== 1) issues.push({ message: 'Choose one primary delivery address.', step: 2 });
    return issues;
  }

  get reviewApproved(): boolean { return this.approvedSnapshot === JSON.stringify(this.buildFullPayload()); }
  stepComplete(index: number): boolean {
    if (index === 0) return this.CustomerOrderForm.valid;
    if (index === 1) return !this.validationIssues.some(issue => issue.step === 1);
    if (index === 2) return !this.validationIssues.some(issue => issue.step === 2);
    if (index === 3) return this.requiredFieldsComplete && !this.hasPendingPriceApproval && !this.pricesNeedValidation;
    return this.reviewApproved && this.requiredFieldsComplete;
  }
  approveReview(approved: boolean) { this.approvedSnapshot = approved ? JSON.stringify(this.buildFullPayload()) : ''; }
  handlingLabel(option: string): string {
    return ({ ShrinkWrap: 'Shrink wrap', SlipSheet: 'Slip sheets', Palletize: 'Palletize', MorningPickup: 'Morning pickup', ReweighOrder: 'Reweight', CustomerPickup: 'Customer pickup' } as Record<string, string>)[option];
  }
  get pricesNeedValidation(): boolean { return this.savedSnapshot !== JSON.stringify(this.buildFullPayload()); }
  get totalOrderWeight(): number | null {
    if(this.OrderLineItems.some(i=>!Number.isFinite(i.NetWeight)||i.NetWeight<=0)) return null;
    return this.OrderLineItems.reduce((sum,i)=>sum+i.NumberOfBags*i.NetWeight,0);
  }
  get totalBoxes(): number { return this.OrderLineItems.filter(i=>Number(i.NetWeight)===22).reduce((sum,i)=>sum+Number(i.NumberOfBags),0); }
  get totalBagsOnly(): number { return this.totalOrderBags-this.totalBoxes; }
  get totalOrderAmount(): number | null {
    if (this.OrderLineItems.some(i => !Number.isFinite(i.NetWeight) || i.NetWeight <= 0)) return null;
    return this.OrderLineItems.reduce((sum, i) => sum + i.NumberOfBags * i.NetWeight * i.Price, 0);
  }

  get hasPendingPriceApproval(): boolean {
    return this.PriceExceptions.some(e => e.ApprovalStatus === 'Pending');
  }

  get selectedPaymentMethodName(): string {
    const id = this.CustomerOrderForm.get('PaymentMethodID')?.value;
    return this.PaymentMethods.find(p => p.PaymentMethodID === id)?.MethodName ?? '—';
  }

  get selectedPaymentTermName(): string {
    const id = this.CustomerOrderForm.get('PaymentTerms')?.value;
    return this.PaymentTerms.find(p => p.PaymentTermId === id)?.PaymentTermName ?? '—';
  }

  get selectedTraderName(): string {
    const id = this.CustomerOrderForm.get('Trader')?.value;
    return this.Traders.find(t => t.TraderID === id)?.FullName ?? '—';
  }

  get selectedWarehouseName(): string {
    return this.Warehouses.find(w => w.LocationID === this.WarehouseNumber)?.LocationName ?? '—';
  }

  get selectedTruckerName(): string {
    const id = this.CustomerOrderForm.get('Trucker')?.value;
    return this.Truckers.find(t => t.TruckerID === id)?.TruckerName ?? '—';
  }

  get totalOrderBags(): number {
    return this.OrderLineItems.reduce((sum, i) => sum + (i.NumberOfBags ?? 0), 0);
  }

  get submissionBlockReason(): string {
    if (!this.requiredFieldsComplete) return this.validationIssues[0]?.message || 'Complete the required order fields.';
    if (this.hasPendingPriceApproval) return 'Senior price approval is required before submission.';
    if (!this.reviewApproved) return 'Check the review confirmation above to enable submission.';
    if (!this.LiveBoardSubmission && this.CustomerOrderForm.get('PaymentMethodID')?.value !== 1) {
      if (!this.DatePaid) return 'Payment must be confirmed before sending to outbound.';
      if (this.pricesNeedValidation) return 'Save your changes to validate pricing before sending to outbound.';
    }
    return '';
  }

  get canSendToOutbound(): boolean {
    if (!this.requiredFieldsComplete || !this.reviewApproved) return false;
    if (this.hasPendingPriceApproval) return false;
    return this.LiveBoardSubmission || this.CustomerOrderForm.get('PaymentMethodID')?.value === 1 || (!!this.DatePaid && !this.pricesNeedValidation);
  }

  // ---- Add new carrier inline (5.2.23) ----
  startAddTrucker() {
    this.AddingTrucker = true;
    this.NewTruckerName = '';
  }

  async saveNewTrucker() {
    if (!this.NewTruckerName?.trim()) return;

    await lastValueFrom(
      this.webService.getWebCall('venders', 'merge_trucker', {
        body: { truckerJson: JSON.stringify({ TruckerName: this.NewTruckerName, IsActive: true }) }
      })
    );

    this.AddingTrucker = false;

    // Re-pull the trucker list so the new one shows up immediately
    const lookupRef: any = await this.inventoryData.getData({ keys: ['Truckers'], includeStatic: false });
    this.Truckers = (lookupRef.Truckers ?? []).filter((t: any) => t.IsActive);
  }

  private buildFullPayload() {
    const formValue = this.CustomerOrderForm.getRawValue();
    const shippingValue = this.ShippingDeliveryForm.getRawValue();

    return {
      OrderID: this.ActiveOrderID ?? undefined,
      CompanyID: formValue.CompanyID,
      SaleDate: this.ActiveOrderID ? undefined : this.newOrderSaleDate,
      Trader: formValue.Trader,
      OrderSource: formValue.OrderSource,
      ProposedPickupDate: formValue.ProposedPickupDate,
      PickupDate: shippingValue.PickupDate,
      PaymentMethodID: formValue.PaymentMethodID,
      PaymentTerms: formValue.PaymentTerms,
      WarehouseNumber: this.WarehouseNumber,
      Trucker: formValue.Trucker,
      WarehouseInstructions: shippingValue.WarehouseInstructions,
      InvoiceInstructions1: shippingValue.InvoiceInstructions1,
      InvoiceInstructions2: shippingValue.InvoiceInstructions2,
      ShrinkWrap: shippingValue.ShrinkWrap,
      SlipSheet: shippingValue.SlipSheet,
      Palletize: shippingValue.Palletize,
      MorningPickup: shippingValue.MorningPickup,
      CustomerPickup: shippingValue.CustomerPickup,
      ReweighOrder: shippingValue.ReweighOrder,
      TruckingQuote: shippingValue.TruckingQuote,
      ProNumber: shippingValue.ProNumber,
      LineItems: this.OrderLineItems.map(i => ({ InventoryID: i.InventoryID, Price: i.Price, NumberOfBags: i.NumberOfBags, ReserveOrderDetailID: i.ReserveOrderDetailID ?? null })),
      ShippingAddresses: this.ShippingAddresses,
      BillingAddressID:this.BillingAddressID,ShippingAddressID:this.ShippingAddressID,
    };
  }

  private async persistFullOrder(): Promise<number | null> {
    const payload = this.buildFullPayload();
    const result = await lastValueFrom(this.ordersData.mergeOrder(payload));
    this.ActiveOrderID = result?.OrderID ?? this.ActiveOrderID;
    return this.ActiveOrderID;
  }

  private async refreshServerState() {
    if (!this.ActiveOrderID) return;
    const result = await lastValueFrom(this.ordersData.getOrderDetails(this.ActiveOrderID));
    this.OrderLineItems = result?.lineItems ?? this.OrderLineItems;
    this.PriceExceptions = result?.order?.PriceExceptions ?? [];
    this.OrderStatusName = result?.order?.StatusName ?? this.OrderStatusName;
    this.BoardOrderStatusID = Number(result?.order?.OrderStatusID ?? 1);
    this.DatePaid = result?.order?.DatePaid ?? null;
    this.savedSnapshot = JSON.stringify(this.buildFullPayload());
  }

  async saveDraft() {
    if (this.Saving || this.Submitting) return;
    if (!this.CustomerOrderForm.get('CompanyID')?.value) return;

    const confirmPrompt = this.prompt.open({
      promptText: 'Save this order so you can continue it later? It will not be sent to outbound or the warehouse.',
      buttons: [{ label: 'Continue', value: true, buttonClass: 'btn-bg-green' }]
    });
    const confirmed = await firstValueFrom(confirmPrompt.closed$.pipe(take(1)));
    if (!confirmed) return;
    if (this.Saving || this.Submitting) return;

    this.Saving = true;
    this.ActionError = '';
    try {
      await this.persistFullOrder();
      await this.refreshServerState();
    } catch (error: any) {
      this.ActionError = error?.error?.error ?? 'The draft could not be saved. Your entered information is still here; please try again.';
    } finally {
      this.Saving = false;
    }
    this.cdr.markForCheck();
  }

  async submitToOutbound() {
    if (!this.canSendToOutbound || this.Saving || this.Submitting) return;

    this.Submitting = true;
    this.ActionError = '';
    try {
      const savedId = this.pricesNeedValidation ? await this.persistFullOrder() : this.ActiveOrderID;
      if (!savedId) return;
      await this.refreshServerState();

      if (this.hasPendingPriceApproval) {
        this.prompt.open({
          promptText: 'One or more line items are priced outside the approved range and now require senior approval before this order can be sent. See Step 4 for details.',
          buttons: [{ label: 'OK', value: true, buttonClass: 'btn-bg-blue' }]
        });
        return;
      }

      if (this.LiveBoardSubmission) {
        await lastValueFrom(this.webService.getWebCall('order-flow', savedId + '/transition', {
          body: { Action: 'submit', ExpectedStatus: this.BoardOrderStatusID, ActionByGUID: localStorage.getItem('GUID') }
        }));
      } else await lastValueFrom(this.ordersData.sendToOutbound(savedId));
      this.pulldownRef.close(savedId);
    } catch (error: any) {
      this.ActionError = error?.error?.error ?? 'The order could not be sent. Please review the order and try again.';
    } finally {
      this.Submitting = false;
    }
    this.cdr.markForCheck();
  }

  async cancelThisOrder() {
    if (!this.ActiveOrderID || this.Saving || this.Submitting) return;

    const reasonPrompt = this.prompt.open({
      promptText: 'Reason for cancelling this order (optional):',
      promptInput: 'text',
      buttons: [{ label: 'Cancel Order', value: true, buttonClass: 'btn-bg-red' }]
    });
    const result: any = await firstValueFrom(reasonPrompt.closed$.pipe(take(1)));
    if (!result) return;
    if (this.Saving || this.Submitting) return;

    this.Submitting = true;
    try {
      await lastValueFrom(this.ordersData.cancelOrder(this.ActiveOrderID, result.inputValue));
      this.pulldownRef.close(this.ActiveOrderID);
    } finally {
      this.Submitting = false;
    }
    this.cdr.markForCheck();
  }

  returnToList() {
    this.pulldownRef.close(this.ActiveOrderID);
  }
}

